import type { AxiosRequestConfig } from 'axios';

import { filterUndefinedParams } from './utils';
import axios from '../config/axiosPrivate';
import { CommentPayloadType, CommentType } from '../types/comments';
import { CommunityContactPayloadType } from '../types/communities';
import { CommunityLevelFeeAPIParams } from '../types/communityLevelFees';

export const getCities = async (params: any = {}) => {
  const response = await axios.get('/communities/cities', { params });
  return response.data;
};

export const getCommunityCheck = async (params: any) => {
  const response = await axios.get(`/communities/check`, { params });
  return response.data;
};

export const getCommunity = async (communityId: number, params: any = {}) => {
  const response = await axios.get(`/communities/${communityId}`, { params });
  return response.data;
};

export const createCommunity = async (payload: any) => {
  const url = `/communities/`;

  const response = await axios.post(url, payload);
  return response.data;
};

export const updateCommunity = async (communityId: number, payload: any) => {
  const url = `/communities/${communityId}`;

  const response = await axios.patch(url, payload);
  return response.data;
};

export const getCompetitors = async (communityId: number, params: any = {}) => {
  const response = await axios.get(`/communities/${communityId}/competitors`, { params });
  return response.data;
};

export const getCommunityImages = async (communityId: number, params: any) => {
  const url = `/communities/Get_CommunityCompareImages/${communityId}/`;
  const response = await axios.get(url, { params });
  return response.data;
};

export const getAllCommunities = async (params: any) => {
  const url = '/communities/';
  const response = await axios.get(url, { params });
  return response.data;
};

export const getAllCommunitiesMinimal = async (params: any = {}) => {
  const url = '/communities/all';
  const response = await axios.get(url, { params });
  return response.data;
};

export const getUserCommunities = async (params: any) => {
  const url = '/communities/user-communities';
  const response = await axios.get(url, { params });
  return response.data;
};

export const getAllCommunitiesStats = async () => {
  const url = '/communities/stats';
  const response = await axios.get(url);
  return response.data;
};

export const getCommunityAudits = async (communityId: number, params?: { type?: number[]; status?: number[] }) => {
  const response = await axios.get(`/communities/${communityId}/audits`, {
    params,
    paramsSerializer: {
      indexes: null,
    },
  });
  return response.data;
};

export const runCommunityAudit = async (communityId: number) => {
  const response = await axios.post(`/communities/${communityId}/audits/run`);
  return response.data;
};

export const getCommunityLevelFees = async (communityId: number, params?: { active?: number }) => {
  const cleanParams = filterUndefinedParams(params);
  const query = new URLSearchParams(cleanParams).toString();

  const url = `communities/${communityId}/community-level-fees?${query}`;
  const response = await axios.get(url);
  return response.data;
};

export const createCommunityLevelFees = async (communityId: number, params: CommunityLevelFeeAPIParams) => {
  const url = `communities/${communityId}/community-level-fees`;
  const response = await axios.post(url, params);
  return response.data;
};

export const updateCommunityLevelFees = async (communityId: number, params: CommunityLevelFeeAPIParams) => {
  const url = `communities/${communityId}/community-level-fees/${params.id}`;
  const response = await axios.patch(url, params);
  return response.data;
};

export const getOccupancies = async (communityId: number, params: any = {}, config?: AxiosRequestConfig) => {
  const response = await axios.get(`/communities/${communityId}/occupancies`, {
    ...(config ?? {}),
    params,
  });
  return response.data;
};

export const getFeesIncentives = async (communityId: number, params: any = {}, config?: AxiosRequestConfig) => {
  const response = await axios.get(`/communities/${communityId}/fee-incentives`, {
    ...(config ?? {}),
    params,
  });
  return response.data;
};

export const getFees = async (communityId: number, params: any = {}, config?: AxiosRequestConfig) => {
  const response = await axios.get(`/communities/${communityId}/fees`, {
    ...(config ?? {}),
    params,
  });
  return response.data;
};

export const getLivingInfo = async (communityId: number, params: any = {}, config?: AxiosRequestConfig) => {
  const response = await axios.get(`/communities/${communityId}/living-info`, {
    ...(config ?? {}),
    params,
  });
  return response.data;
};

export const getAdditionalLinks = async (communityId: number) => {
  const response = await axios.get(`/communities/${communityId}/additional-links`);
  return response.data;
};

export const bulkAdditionalLinks = async (communityId: number, payload: any) => {
  const url = `/communities/${communityId}/additional-links`;
  const response = await axios.post(url, payload);
  return response.data;
};

export const getCommunityContacts = async (communityId: number) => {
  const response = await axios.get(`/communities/${communityId}/contacts`);
  return response.data;
};

export const createCommunityContact = async (communityId: number, payload: CommunityContactPayloadType) => {
  const response = await axios.post(`/communities/${communityId}/contacts`, payload);
  return response.data;
};

export const updateCommunityContact = async (
  communityId: number,
  communityContactId: number,
  payload: CommunityContactPayloadType
) => {
  const response = await axios.patch(`/communities/${communityId}/contacts/${communityContactId}`, payload);
  return response.data;
};

export const deleteCommunityContact = async (communityId: number, communityContactId: number) => {
  const response = await axios.delete(`/communities/${communityId}/contacts/${communityContactId}`);
  return response.data;
};

export const getCommunityPhotos = async (communityId: number, params: any) => {
  const response = await axios.get(`/communities/${communityId}/photos`, { params });
  return response.data;
};

export const createPhotos = async (communityId: number, payload: any) => {
  const url = `/communities/${communityId}/photos`;
  const response = await axios.post(url, payload);
  return response.data;
};

export const getIncentives = async (communityId: number, params: any, config?: AxiosRequestConfig) => {
  const url = `/pricingassistant/${communityId}/incentives`;
  const response = await axios.get(url, {
    ...(config ?? {}),
    params,
  });
  return response.data;
};

export const createIncentive = async (communityId: number, payload: any) => {
  const url = `/pricingassistant/${communityId}/incentives`;
  const response = await axios.post(url, payload);
  return response.data;
};

export const updateIncentive = async (communityId: number, incentiveId: number, payload: any) => {
  const url = `/pricingassistant/${communityId}/incentives/${incentiveId}`;
  const response = await axios.put(url, payload);
  return response.data;
};

export const deleteIncentive = async (communityId: number, incentiveId: number) => {
  const url = `/pricingassistant/${communityId}/incentives/${incentiveId}`;
  const response = await axios.delete(url);
  return response.data;
};

export const getCommunityBrochureAndDocs = async (communityId: number, params: any) => {
  const response = await axios.get(`/communities/${communityId}/files`, { params });
  return response.data;
};

export const getCommunityFileCategories = async () => {
  const response = await axios.get('/communities/files/categories');
  return response.data;
};

export const analyzeCommunityFiles = async (communityId: number, payload: any) => {
  const response = await axios.post(`/communities/${communityId}/files/analyze`, payload);
  return response.data;
};

export const createCommunityFiles = async (communityId: number, payload: any) => {
  const response = await axios.post(`/communities/${communityId}/files`, payload);
  return response.data;
};

export const getContractBenefits = async (communityId: number, params?: any, config?: AxiosRequestConfig) => {
  const response = await axios.get(`/contracts/${communityId}/contract-benefits`, {
    ...(config ?? {}),
    params,
  });
  return response.data;
};

export const getMostCommonCommunityContract = async (communityId: number, params?: any) => {
  const url = `/contracts/${communityId}/most-common-contract`;
  const response = await axios.get(url, { params });
  return response.data;
};

export const getCompanyRentalTypeMetrics = async () => {
  const url = `/contracts/metrics`;
  const response = await axios.get(url);
  return response.data;
};

export const getContractBenefitCategories = async () => {
  const url = `/contracts/benefit-categories`;
  const response = await axios.get(url);
  return response.data;
};

export const getCommunitiesPreferences = async (params: any) => {
  const url = '/communities/preferences';
  const response = await axios.get(url, { params });
  return response.data;
};

export const bulkUpdateCommunityPreferences = async (payload: any) => {
  const url = `/communities/preferences/bulk`;

  const response = await axios.post(url, payload);
  return response.data;
};

export const getCommunitiesEmailNotificationRecipients = async (notificationType: number, params: any) => {
  const url = `/communities/preferences/email_recipients/${notificationType}`;
  const response = await axios.get(url, { params });
  return response.data;
};

export const saveCommunitiesEmailNotificationRecipients = async (notificationType: number, payload: any) => {
  const url = `/communities/preferences/email_recipients/${notificationType}`;
  const response = await axios.post(url, payload);
  return response.data;
};

export const getCommunityIncentiveNotificationRecipients = async (communityId: number) => {
  const url = `/communities/preferences/${communityId}/incentive-notification-recipients`;
  const response = await axios.get(url);
  return response.data;
};

export const getCommunityNotificationRecipients = async (communityId: number, notificationType: number) => {
  const url = `/communities/preferences/${communityId}/notification_recipients/${notificationType}`;
  const response = await axios.get(url);
  return response.data;
};

export const sendEmailNotification = async (communityId: number, notificationType: number, payload: any) => {
  const url = `/communities/preferences/${communityId}/send_email_notification/${notificationType}`;

  const response = await axios.post(url, payload);
  return response.data;
};

export const getCommunityComments = async (communityId: number): Promise<CommentType[]> => {
  const response = await axios.get<CommentType[]>(`/communities/${communityId}/comments`);
  return response.data;
};

export const postCommunityComment = async (communityId: number, payload: CommentPayloadType): Promise<CommentType> => {
  const response = await axios.post<CommentType>(`/communities/${communityId}/comments`, payload);
  return response.data;
};

export const updateCommunityComment = async (
  communityId: number,
  commentId: number,
  payload: CommentPayloadType
): Promise<CommentType> => {
  const response = await axios.patch<CommentType>(`/communities/${communityId}/comments/${commentId}`, payload);
  return response.data;
};

export const deleteCommunityComment = async (communityId: number, commentId: number) => {
  const response = await axios.delete(`/communities/${communityId}/comments/${commentId}`);
  return response.data;
};
